/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { GetAllVendorsListApiResponse } from "../../../@types/DRO/GetAllVendorsResponse";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import {
    GetSingleOrderDetailsObject,
    PurchaseOrderLine,
} from "../../../@types/DRO/PurchaseOrderLinesResponse";
import { CreateDeliveryOrderBody } from "../../../@types/DTO/CreateDeliveryOrderBody";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { UIDPartApi } from "../../../api/UID-Part/UIDPartApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface CreateDeliveryOrderProps {
  onBack: () => void;
  orderId: number | null;
}

interface OrderItem {
  partId: number;
  description: string;
  referenceCode?: string;
  quantity: number;
  received: number;
  billed: number;
  unit: string;
  price: number;
  taxes: number;
  total: number;
  demandLeft?: number;
  unitPrice?: number;
}

const initialNewItem: OrderItem = {
  partId: 0,
  description: "",
  referenceCode: "",
  quantity: 0,
  received: 0,
  billed: 0,
  unit: "Units",
  price: 0,
  taxes: 0,
  total: 0,
  demandLeft: 0,
  unitPrice: 0,
};

export const CreateDeliveryOrder: React.FC<CreateDeliveryOrderProps> = ({
  onBack,
  orderId,
}) => {
  const [items, setItems] = useState<OrderItem[]>([]);
  const [originalItems, setOriginalItems] = useState<OrderItem[]>([]);
  const [newItem, setNewItem] = useState<OrderItem>(initialNewItem);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const navigate = useNavigate();
  const [sourceDocId, setSourceDocId] = useState<number | null>(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [docketNumber, setDocketNumber] = useState("");
  const [courierName, setCourierName] = useState("");
  const [attachmentId, setAttachmentId] = useState<number | null>(null);
  const [attachmentFileName, setAttachmentFileName] = useState("");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [parts, setParts] = useState<TransferOrderPartDetails[]>([]);
  const [destinationLocations, setDestinationLocations] = useState<Warehouse[]>(
    []
  );
  const [deliveryWarehouseId, setDeliverWareHouseId] = useState<number | null>(
    null
  );
  const { setLoading } = useLoading();
  const [vendors, setVendors] = useState<
    GetAllVendorsListApiResponse["data"]["data"]
  >([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState<{
    vendorId: number;
    vendorName: string;
  } | null>(null);
  const [singleOrderDetails, setSingleOrderDetails] =
    useState<GetSingleOrderDetailsObject>();
  const [billedValues, setBilledValues] = useState<Record<number, string>>({});
  const partAutocompleteRef = useRef<any>(null);
  const [selectedPart, setSelectedPart] =
    useState<TransferOrderPartDetails | null>(null);
  const [hasMoreParts, setHasMoreParts] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddItemRow, setShowAddItemRow] = useState(false);
  const [availableItemsFromOrder, setAvailableItemsFromOrder] = useState<
    OrderItem[]
  >([]);

  // Helper function to calculate cost based on quantity, price, and tax
  const calculateCost = (quantity: number, price: number=0, tax: number): number => {
    const baseTotal = quantity * price;
    const taxAmount = (baseTotal * tax) / 100;
    return baseTotal + taxAmount;
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingAttachment(true);
      const uploadResponse = await UIDPartApi.api.uploadFile(file);
      const fileId = uploadResponse?.fileId ?? uploadResponse?.data?.fileId;

      if (!fileId) {
        throw new Error("Failed to get attachment file ID from server response");
      }

      setAttachmentId(fileId);
      setAttachmentFileName(file.name);
      setSnackbar({
        open: true,
        message: "Attachment uploaded successfully",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Attachment upload failed", error);
      setAttachmentId(null);
      setAttachmentFileName("");
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload attachment",
        severity: "error",
      });
    } finally {
      setIsUploadingAttachment(false);
      event.target.value = "";
    }
  };

  const handleDeleteItem = (index: number) => {
    const deletedItem = items[index];

    setItems((prev) => prev.filter((_, i) => i !== index));
    setBilledValues((prev) => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    setAvailableItemsFromOrder((prev) => [...prev, deletedItem]);
    setShowAddItem(true);
    setShowAddItemRow(false);
  };

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const target = event.currentTarget;
    const bottom =
      target.scrollHeight - target.scrollTop === target.clientHeight;
    if (bottom) setPage((prev) => prev + 1);
  };

  const createDeliveryOrder = useCallback(
    async (status: string) => {
      if (!deliveryWarehouseId) {
        setSnackbar({
          open: true,
          message: "Please select a destination location",
          severity: "error",
        });
        return;
      }

      if (!sourceDocId) {
        setSnackbar({
          open: true,
          message: "Source Document ID is required",
          severity: "error",
        });
        return;
      }

      if (!scheduledDate) {
        setSnackbar({
          open: true,
          message: "Please select a scheduled date",
          severity: "error",
        });
        return;
      }

      if (items.length === 0) {
        setSnackbar({
          open: true,
          message: "Please add at least one item",
          severity: "error",
        });
        return;
      }

      // Validate that at least one item has a quantity > 0
      const hasValidQuantity = items.some(item => item.billed > 0);
      if (!hasValidQuantity) {
        setSnackbar({
          open: true,
          message: "At least one item must have a quantity greater than 0",
          severity: "error",
        });
        return;
      }

      let finalItems = [...items];
      if (showAddItem && selectedPart && newItem.billed > 0) {
        const itemFromOrder = availableItemsFromOrder.find(
          (item) => item.partId === selectedPart.id
        );
        if (itemFromOrder) {
          finalItems.push({
            ...itemFromOrder,
            billed: newItem.billed,
          });
        }
      }

      setLoading(true);
      const createDeliveryOrderBody: CreateDeliveryOrderBody = {
        purchaseOrderId: sourceDocId,
        status,
        deliveryWarehouseId,
        deliveryOrderLineRequests: finalItems
          .filter(item => item.billed > 0) 
          .map((item) => ({
            partId: item.partId,
            quantityOrdered: item.billed, 
          })),
        parentId: null,
        scheduledDate: scheduledDate,
        ...(docketNumber.trim() && { docketNumber: docketNumber.trim() }),
        ...(courierName.trim() && { courierName: courierName.trim() }),
        ...(attachmentId && { attachment: attachmentId }),
      };

      try {
        const response = await DeliveryOrderApi.api.createDeliveryOrder(
          createDeliveryOrderBody
        );
        setSnackbar({
          open: true,
          message: response?.message || "Delivery order created successfully",
          severity: "success",
        });
        // setTimeout(() => navigate("/delivery-order", { replace: true }), 2000);
        setTimeout(() => onBack(), 2000);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message || "Error creating delivery order",
          severity: "error",
        });
        console.error("Error creating delivery order", error);
      } finally {
        setLoading(false);
      }
    },
    [
      deliveryWarehouseId,
      items,
      sourceDocId,
      scheduledDate,
      navigate,
      setLoading,
      showAddItem,
      selectedPart,
      newItem.billed,
      availableItemsFromOrder,
      docketNumber,
      courierName,
      attachmentId,
      isUploadingAttachment,
    ]
  );

  const singleOrderData = useCallback(async () => {
    if (!orderId) return;

    try {
      const response = await ProductOrderApi.api.getSingleProductOrderDetails(
        orderId
      );
      if (response?.data) {
        setSingleOrderDetails(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching order details", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error fetching order details",
        severity: "error",
      });
    }
  }, [orderId]);

  useEffect(() => {
    singleOrderData();
  }, [singleOrderData]);

  const parseList = useCallback(
    (purchaseOrderList: PurchaseOrderLine[] = []) => {
      return purchaseOrderList.map((item) => {
        const partId = item.partId || 0;
        const partName = item?.partName || "N/A";
        const referenceCode = item?.referenceCode || "";
        const quantityOrdered = item?.quantityOrdered || 0;
        const price = item?.price || 0;
        const tax = item?.tax || 0;
        const demandLeft = item?.demandLeft || 0;
        const unitPrice=item?.unitPrice||0;
        const baseTotal = quantityOrdered * price;
        const taxAmount = (baseTotal * tax) / 100;
        const totalWithTax = baseTotal + taxAmount;

        return {
          partId,
          description: partName,
          referenceCode,
          quantity: quantityOrdered,
          received: 0,
          billed: 0,
          unit: "Units",
          unitPrice,
          price: parseFloat(price.toFixed(2)),
          taxes: parseFloat(tax.toFixed(2)),
          total: parseFloat(totalWithTax.toFixed(2)),
          demandLeft: demandLeft,
        };
      });
    },
    []
  );

  const initializeFormFromOrderDetails = useCallback(() => {
    if (!singleOrderDetails) return;

    setSourceDocId(singleOrderDetails.id);

    setDeliverWareHouseId(
      singleOrderDetails.deliveryWareHouseDetails?.deliveryWarehouseId || null
    );
    setScheduledDate(singleOrderDetails.orderDate);

    if (singleOrderDetails.vendorDetails) {
      setSelectedVendor({
        vendorId: singleOrderDetails.vendorDetails.vendorId,
        vendorName: singleOrderDetails.vendorDetails.vendorName,
      });
    }

    if (singleOrderDetails.purchaseOrderLines) {
      const parsedItems = parseList(singleOrderDetails.purchaseOrderLines);
      setItems(parsedItems);
      setOriginalItems(parsedItems);
      setAvailableItemsFromOrder([]);
      setShowAddItem(false);
      setShowAddItemRow(false);
    }
  }, [singleOrderDetails, parseList]);

  useEffect(() => {
    initializeFormFromOrderDetails();
  }, [initializeFormFromOrderDetails]);

  const handleBilledChange = useCallback(
    (index: number, value: string, demandLeft: number) => {
      const numericValue = value === "" ? "" : value.replace(/[^0-9]/g, "");
      const maxAllowed = demandLeft || 0;

      if (
        numericValue === "" ||
        (Number(numericValue) <= maxAllowed && Number(numericValue) >= 0)
      ) {
        setBilledValues((prev) => ({ ...prev, [index]: numericValue }));
        setItems((prev) => {
          const updatedItems = [...prev];
          const item = updatedItems[index];
          const billedQty = numericValue === "" ? 0 : Number(numericValue);


          // Calculate cost dynamically based on quantity expected, price, and tax
          const totalWithTax = calculateCost(billedQty, item.unitPrice, item.taxes);

          updatedItems[index] = {
            ...item,
            billed: billedQty,
            total: parseFloat(totalWithTax.toFixed(2)),
          };

          return updatedItems;
        });
      }
    },
    []
  );

  const fetchDestinationLocations = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList();
      if (response?.data) {
        setDestinationLocations(response.data?.data);
      }
    } catch (error: any) {
      console.error("Error fetching destination locations", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Error fetching destination locations",
        severity: "error",
      });
    }
  }, []);

  const fetchParts = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      const filteredItems = availableItemsFromOrder.filter((item) =>
        item.description.toLowerCase().includes(search.toLowerCase())
      );

      const formattedParts: TransferOrderPartDetails[] = filteredItems.map(
        (item) => ({
          id: item.partId,
          description: item.description,
          costPrice: item.price,
          averageCostPrice: 0,
          tax: item.taxes,
          partNumber: "",
          internalPartNumber: "",
          quantity: item.quantity,
          modelType: "",
          oemId: 0,
          oemName: "",
          modelTypeId: 0,
          createdDate: "",
          updatedDate: "",
          externalId: 0,
          referenceCode: "",
          type: "",
          status: 0,
          warehouseId: 0,
          warehouseName: "",
          vendorId: 0,
          vendorName: "",
          minQuantity: 0,
          maxQuantity: 0,
          salesPrice: 0,
          price: 0,
          penaltyType: "",
          deleted: false,
          updatedBy: 0,
        })
      );

      setParts(formattedParts);
      setHasMoreParts(false);
    },
    [availableItemsFromOrder]
  );

  const fetchVendors = useCallback(
    async (resetPage = false) => {
      try {
        const response = await GetAllVendorsListApi.api.getAllVendorsList(
          resetPage ? 1 : page + 1,
          rowsPerPage,
          searchQuery
        );
        setVendors((prev) =>
          resetPage ? response.data.data : [...prev, ...response.data.data]
        );
      } catch (error: any) {
        console.error("Error fetching vendors", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Error fetching vendors",
          severity: "error",
        });
      }
    },
    [page, rowsPerPage, searchQuery]
  );

  useEffect(() => {
    fetchDestinationLocations();
    fetchVendors();
  }, [fetchDestinationLocations, fetchVendors]);

  useEffect(() => {
    if (showAddItemRow) {
      fetchParts(0, "", true);
    }
  }, [fetchParts, showAddItemRow, availableItemsFromOrder]);

  const debouncedPartSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchParts(0, searchValue, true);
    }, 500),
    [fetchParts]
  );

  const handlePartSelect = (
    event: any,
    newValue: TransferOrderPartDetails | null
  ) => {
    if (newValue) {
      const itemFromOrder = availableItemsFromOrder.find(
        (item) => item.partId === newValue.id
      );

      if (itemFromOrder) {
        setItems([...items, itemFromOrder]);
        setAvailableItemsFromOrder((prev) =>
          prev.filter((item) => item.partId !== newValue.id)
        );
        setSelectedPart(null);
        setShowAddItemRow(false);

        if (availableItemsFromOrder.length <= 1) {
          setShowAddItem(false);
        }
      }
    }
  };

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", path: "/delivery-order", clickHandler: onBack },
      { label: "Orders", path: "/delivery-order", clickHandler: onBack },
      {
        label: (orderId ? "Edit" : "Create") + " Order",
        path: "",
        clickHandler: null,
      },
    ],
    [orderId, onBack]
  );

  // Get the cost for display - calculate dynamically
  const getDisplayCost = (item: OrderItem): string => {
    const cost = calculateCost(item.billed, item.unitPrice, item.taxes);
    return cost.toFixed(2);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        <Box sx={{ mb: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                textAlign: "left",
                color: "#1E1E1E",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              Create Delivery Order
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F8F9FA",
              padding: "12px 16px",
              borderRadius: "8px",
            }}>
            <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {crumb.clickHandler ? (
                    <button
                      onClick={crumb.clickHandler}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="text-sm font-medium text-gray-700">
                      {crumb.label}
                    </span>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <span className="text-sm text-gray-400">/</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Box>
        </Box>

        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Destination Location
                  </label>
                  <select
                    value={deliveryWarehouseId || ""}
                    disabled
                    className="w-full border-b focus:outline-none p-1 text-sm">
                    <option value="">Select Location</option>
                    {destinationLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    PO Id
                  </label>
                  <input
                    type="text"
                    value={sourceDocId || ""}
                    disabled
                    className="w-full border-b focus:outline-none p-1 text-sm"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Scheduled Date <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    fullWidth
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    variant="standard"
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Docket Number"
                  value={docketNumber}
                  onChange={(e) => setDocketNumber(e.target.value)}
                  variant="standard"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Courier Name"
                  value={courierName}
                  onChange={(e) => setCourierName(e.target.value)}
                  variant="standard"
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", justifyContent: "flex-start" }}>
                <input
                  ref={attachmentInputRef}
                  type="file"
                  hidden
                  onChange={handleAttachmentChange}
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={isUploadingAttachment}
                    sx={{
                      width: 150,
                      height: 42,
                      justifyContent: "center",
                      px: 2,
                      borderRadius: "999px",
                      background: "linear-gradient(135deg, #16a34a, #0f766e)",
                      boxShadow: "0 6px 14px rgba(22, 163, 74, 0.22)",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        background: "linear-gradient(135deg, #15803d, #115e59)",
                        boxShadow: "0 8px 18px rgba(22, 163, 74, 0.3)",
                      },
                    }}>
                    {isUploadingAttachment ? "Uploading..." : "Upload LR"}
                  </Button>
                  {attachmentFileName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}>
                      {attachmentFileName}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Vendor Name
                  </label>
                  <Autocomplete
                    fullWidth
                    disabled
                    options={selectedVendor ? [selectedVendor] : []}
                    getOptionLabel={(option) => option.vendorName}
                    value={selectedVendor}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                    onScroll={handleScroll}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#ffffff" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 20,
                      }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 180,
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 120,
                      }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 90,
                      }}>
                      Demand
                    </TableCell>
                    {orderId && (
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          minWidth: 100,
                        }}>
                        Demand Left
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 100,
                      }}>
                      Quantity Expected
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 100,
                      }}>
                      Unit of Measure
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 100,
                      }}>
                      Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 80,
                      }}>
                      Tax (%)
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 100,
                      }}>
                      Cost
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: 80,
                      }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#ffffff" },
                        "&:hover": { backgroundColor: "#e3f2fd" },
                      }}>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.referenceCode || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.quantity}
                      </TableCell>
                      {orderId && (
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          <TextField
                            variant="standard"
                            fullWidth
                            value={item.demandLeft || 0}
                            disabled
                            InputProps={{
                              sx: {
                                fontSize: "0.875rem",
                                "& .Mui-disabled": {
                                  WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                                },
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField
                          variant="standard"
                          fullWidth
                          placeholder="Enter Qty"
                          value={billedValues[index] || ""}
                          onChange={(e) =>
                            handleBilledChange(
                              index,
                              e.target.value,
                              item.demandLeft || 0
                            )
                          }
                          inputProps={{
                            maxLength:
                              (item.demandLeft || 0).toString().length + 2,
                            style: {
                              fontSize: "0.875rem",
                              MozAppearance: "textfield",
                              appearance: "textfield",
                            },
                          }}
                          InputProps={{
                            sx: {
                              fontSize: "0.875rem",
                              px: 1,
                              py: 0.5,
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                                "&::-webkit-outer-spin-button": {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                                "&::-webkit-inner-spin-button": {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                              },
                            },
                          }}
                          sx={{
                            minWidth: "100px",
                            "& .MuiInput-root": {
                              "&:before, &:after": {
                                borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                              },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.unit}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        ₹{item.unitPrice}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {item.taxes.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        ₹{getDisplayCost(item)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteItem(index)}
                          sx={{ color: "#FF0000" }}
                          size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {showAddItemRow && (
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell
                        colSpan={orderId ? 10 : 9}
                        sx={{ textAlign: "center", py: 2 }}>
                        <Autocomplete
                          ref={partAutocompleteRef}
                          options={parts}
                          getOptionLabel={(option) => option.description}
                          value={selectedPart}
                          onChange={handlePartSelect}
                          onInputChange={(event, newInputValue) => {
                            debouncedPartSearch(newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              placeholder="Select part to add"
                              sx={{ maxWidth: 400, mx: "auto" }}
                            />
                          )}
                          sx={{ maxWidth: 400, mx: "auto" }}
                          key={availableItemsFromOrder.length}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {showAddItem &&
              !showAddItemRow &&
              availableItemsFromOrder.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddItemRow(true)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "20px",
                      px: 3,
                    }}>
                    Add Item
                  </Button>
                </Box>
              )}

            {showAddItem && availableItemsFromOrder.length === 0 && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No more items available to add
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="error"
              sx={{
                marginRight: 2,
                padding: "8px 20px",
                borderRadius: "32px",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
              onClick={onBack}>
              Close Order
            </Button>
            <Button
              onClick={() => createDeliveryOrder("DRAFT")}
              variant="contained"
              color="primary"
              sx={{
                padding: "8px 20px",
                borderRadius: "32px",
                backgroundColor: "#1E1E1E",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "0.875rem",
                "&:hover": { backgroundColor: "#000000" },
              }}>
              Draft
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => createDeliveryOrder("CREATED")}
              sx={{
                padding: "8px 20px",
                marginLeft: 2,
                borderRadius: "32px",
                backgroundColor: "#1E1E1E",
                textTransform: "none",
                fontWeight: "500",
                fontSize: "0.875rem",
                "&:hover": { backgroundColor: "#000000" },
              }}>
              Create Delivery Order
            </Button>
          </Box>
        </Paper>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateDeliveryOrder;